import type {FC, PropsWithChildren, RefObject} from 'react';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {createPortal} from 'react-dom';
import {type TooltipRefProps} from 'react-tooltip';

import {useTheme} from 'styled-components';

import type {Nullable} from '../../utils/utility-types';
import type {Props, TooltipRef} from './types';

import {getUuid} from '../../utils/getUuid';
import * as S from './styles';
import {isHTMLDivRefObject} from './utils';

const SsrSafePortal: FC<PropsWithChildren> = ({children}) => {
  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(children, document.body);
};

export const Tooltip = forwardRef<TooltipRef, Props>(
  (
    {children, content, disabled, place, webProps = {}, width, zPosition},
    ref,
  ) => {
    const {
      afterHide,
      clickable,
      effect = 'solid',
      isArrowVisible = true,
    } = webProps;

    const theme = useTheme();
    const id = useMemo(() => getUuid(), []);
    const parentRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<TooltipRefProps>(null);
    const [activeParentRef, setActiveParentRef] = useState(parentRef);

    const changeParent = (newParent: RefObject<Nullable<HTMLDivElement>>) => {
      if (activeParentRef?.current) {
        activeParentRef.current.removeAttribute('data-tooltip-content');
        activeParentRef.current.removeAttribute('data-tooltip-id');
      }

      if (newParent.current) {
        newParent.current.setAttribute('data-tooltip-content', '');
        newParent.current.setAttribute('data-tooltip-id', id);
      }

      setActiveParentRef(newParent);
    };

    useImperativeHandle(ref, () => ({
      hide: () => {
        tooltipRef.current?.close();
      },
      show: options => {
        const {from} = options ?? {};

        if (from) {
          if (isHTMLDivRefObject(from)) {
            changeParent(from);

            tooltipRef.current?.open();
          }
        } else {
          if (parentRef?.current) {
            tooltipRef.current?.open();
          }
        }
      },
    }));

    // setup parentRef on mount
    useEffect(() => {
      changeParent(parentRef);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <>
        <S.RefContainer display={webProps.display} ref={parentRef}>
          {children}
        </S.RefContainer>
        <SsrSafePortal>
          <S.Tooltip
            afterHide={afterHide}
            arrowColor={isArrowVisible ? undefined : 'transparent'}
            clickable={clickable}
            float={effect === 'float'}
            hidden={disabled}
            id={id}
            openOnClick={clickable}
            place={place}
            ref={tooltipRef}
            variant={theme.id}
            width={width}
            zPosition={zPosition}
          >
            {typeof content === 'function' ? content() : content}
          </S.Tooltip>
        </SsrSafePortal>
      </>
    );
  },
);
